/* Shared visual enhancement. Native buttons, handlers, forms and child nodes stay in place. */
(() => {
  // Retain native controls in browsers without the crisp contour primitives.
  if (!CSS.supports('clip-path', 'polygon(evenodd, 0 0, 100% 0, 0 100%)')
    || !CSS.supports('width', 'round(nearest, 1cqw, 1px)')) return;
  const scope = document;
  const selector = '.button, .button-secondary, .add-to-cart-button, .email-signup__button, .groovi-bridge__submit';
  const excluded = '.skip-to-content-link, .close-button, .link, .cart-items__remove, .variant-item__remove, .predictive-search__close-modal-button, .dropdown-localization__button, .menu-drawer__back-button, .groovi-truncate__more, .groovi-truncate__less, [slot="more"]';
  const buttons = new Set();
  const pending = new Set();
  let frame;
  const eligible = (node) => node instanceof HTMLElement && node.matches(selector)
    && node.matches('button, a') && !node.matches(excluded)
    && (!node.classList.contains('button-unstyled') || node.classList.contains('email-signup__button'));

  function queue(button) {
    if (!eligible(button)) return;
    pending.add(button);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  // A parent shape may deliberately scale its content (e.g. 95%). Counter that
  // scale on decoration only, so a 2px outline stays 2 screen CSS pixels.
  function canCounterScale(button) {
    for (let node = button; node && node !== document.documentElement; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.rotate !== 'none' && parseFloat(style.rotate) !== 0) return false;
      if (style.scale !== 'none' && style.scale.split(' ').some((value) => parseFloat(value) <= 0)) return false;
      if (style.transform === 'none') continue;
      const matrix = new DOMMatrixReadOnly(style.transform);
      // Preserve deliberate rotations, skews, mirrors and perspective.
      if (!matrix.is2D || Math.abs(matrix.b) > 0.00001 || Math.abs(matrix.c) > 0.00001 || matrix.a <= 0 || matrix.d <= 0) return false;
    }
    return true;
  }

  function refresh(button) {
    if (!button.isConnected) return;
    // Read the owning native/custom styles at the current responsive/interaction state.
    const oldTransition = button.style.getPropertyValue('transition');
    const transitionPriority = button.style.getPropertyPriority('transition');
    button.style.setProperty('transition', 'none', 'important');
    button.classList.remove('groovi-pixel-button');
    const native = getComputedStyle(button);
    const paddingRight = native.paddingRight;
    const paddingBottom = native.paddingBottom;
    const borderLeft = parseFloat(native.borderLeftWidth);
    const borderTop = parseFloat(native.borderTopWidth);
    const clipMargin = Math.max(borderLeft, borderTop, parseFloat(native.borderRightWidth), parseFloat(native.borderBottomWidth));
    let fill = native.backgroundColor;
    if (native.backgroundImage !== 'none') {
      fill = native.backgroundImage.match(/rgba?\([^)]*\)/)?.[0] || fill;
    }
    if (native.position === 'static') button.classList.add('groovi-pixel-positioned');
    button.classList.toggle('groovi-pixel-clipped', native.overflowX !== 'visible' || native.overflowY !== 'visible');
    button.style.setProperty('--groovi-native-padding-right', paddingRight);
    button.style.setProperty('--groovi-native-padding-bottom', paddingBottom);
    button.style.setProperty('--groovi-pixel-fill', fill);
    button.style.setProperty('--groovi-pixel-clip-margin', `${clipMargin}px`);
    for (const side of ['top', 'right', 'bottom', 'left']) button.style.setProperty(`--groovi-native-border-${side}`, native.getPropertyValue(`border-${side}-width`));
    button.classList.add('groovi-pixel-button');

    const surface = button.querySelector(':scope > .groovi-button-frame');
    const box = button.getBoundingClientRect();
    // Commit the enhanced state with transitions still disabled. Otherwise the
    // native hover scale would animate back on every geometry refresh.
    if (oldTransition) button.style.setProperty('transition', oldTransition, transitionPriority);
    else button.style.removeProperty('transition');
    if (!box.width || !box.height || !surface) return;
    const computed = getComputedStyle(button);
    const scaleX = box.width / parseFloat(computed.width);
    const scaleY = box.height / parseFloat(computed.height);
    const dpr = window.devicePixelRatio || 1;
    const shadowOffset = parseFloat(computed.getPropertyValue('--groovi-button-shadow-offset')) || 4;
    const counterScale = canCounterScale(button);
    const left = Math.round(box.left * dpr) / dpr;
    const top = Math.round(box.top * dpr) / dpr;
    // Translation stays in the transform (not layout left/top), avoiding 1/64px
    // layout rounding. Container geometry is measured in screen CSS pixels.
    surface.style.setProperty('--groovi-frame-left', `${-borderLeft}px`);
    surface.style.setProperty('--groovi-frame-top', `${-borderTop}px`);
    surface.style.setProperty('--groovi-frame-shift-x', `${counterScale ? (left - box.left) / scaleX : 0}px`);
    surface.style.setProperty('--groovi-frame-shift-y', `${counterScale ? (top - box.top) / scaleY : 0}px`);
    surface.style.setProperty('--groovi-frame-scale-x', counterScale ? 1 / scaleX : 1);
    surface.style.setProperty('--groovi-frame-scale-y', counterScale ? 1 / scaleY : 1);
    surface.style.setProperty('--groovi-frame-width', `${(counterScale ? Math.round(box.right * dpr) / dpr - left : parseFloat(computed.width)) - shadowOffset}px`);
    surface.style.setProperty('--groovi-frame-height', `${(counterScale ? Math.round(box.bottom * dpr) / dpr - top : parseFloat(computed.height)) - shadowOffset}px`);
  }

  function flush() {
    frame = undefined;
    const work = [...pending];
    pending.clear();
    work.forEach(refresh);
  }

  const resize = new ResizeObserver((entries) => entries.forEach(({ target }) => queue(target)));
  function scan(root) {
    const candidates = [...root.querySelectorAll(selector)];
    if (eligible(root)) candidates.unshift(root);
    for (const button of candidates) {
      if (!eligible(button)) continue;
      // Give anonymous flex text an optical alignment hook, moving the original
      // Text node rather than replacing it or touching native icons/listeners.
      for (const node of [...button.childNodes]) {
        if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) continue;
        const label = document.createElement('span');
        label.className = 'groovi-button-label';
        node.replaceWith(label);
        label.append(node);
      }
      if (!button.querySelector(':scope > .groovi-button-frame')) {
        const surface = document.createElement('span');
        surface.className = 'groovi-button-frame';
        surface.setAttribute('aria-hidden', 'true');
        const shadow = document.createElement('span');
        shadow.className = 'groovi-button-shadow';
        surface.append(shadow);
        button.append(surface);
      }
      const surface = button.querySelector(':scope > .groovi-button-frame');
      if (!surface.querySelector('.groovi-button-shadow')) { const shadow = document.createElement('span'); shadow.className = 'groovi-button-shadow'; surface.append(shadow); }
      button.setAttribute('data-groovi-pixel-ready', '');
      if (!buttons.has(button)) { buttons.add(button); resize.observe(button); }
      queue(button);
    }
  }

  scan(scope);
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') queue(record.target);
      else {
        for (const node of record.addedNodes) if (node instanceof Element) scan(node);
        if (eligible(record.target) && !record.target.querySelector(':scope > .groovi-button-frame')) scan(record.target);
      }
    }
    for (const button of buttons) if (!button.isConnected) { resize.unobserve(button); buttons.delete(button); }
  }).observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'data-added'] });

  for (const event of ['pointerover', 'pointerout', 'focusin', 'focusout']) {
    scope.addEventListener(event, ({ target, relatedTarget }) => {
      const button = target instanceof Element ? target.closest(selector) : null;
      if (button && !(relatedTarget instanceof Node && button.contains(relatedTarget))) queue(button);
    }, { passive: true });
  }
  const refreshAll = () => buttons.forEach(queue);
  addEventListener('resize', refreshAll, { passive: true });
  document.fonts.ready.then(refreshAll);
  document.addEventListener('shopify:section:load', refreshAll);
  let scrollTimer;
  addEventListener('scroll', () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(refreshAll, 100); }, { passive: true });
  scope.addEventListener('transitionend', ({ target, propertyName }) => {
    if (['transform', 'translate', 'scale', 'width', 'height'].includes(propertyName) && target instanceof Element) scan(target);
  }, { passive: true });
})();
